(() => {
  const initialized = new WeakSet();
  function init(root) {
    if (initialized.has(root)) return;
    initialized.add(root);
    const cleanup = [];
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const videos = [...root.querySelectorAll('video[data-src]')];
    const visible = new Set();
    const setPoster = video => { if (video.dataset.poster) { video.poster = video.dataset.poster; delete video.dataset.poster; } };
    // Posters below the first screen (data-poster) load as the visitor nears them, so they don't compete with the hero.
    const posters = [...root.querySelectorAll('video[data-poster]')];
    if ('IntersectionObserver' in window) {
      const posterObserver = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) { setPoster(entry.target); posterObserver.unobserve(entry.target); }
      }), { rootMargin: '1250px 0px' });
      posters.forEach(video => posterObserver.observe(video));
      cleanup.push(() => posterObserver.disconnect());
    } else {
      posters.forEach(setPoster);
    }
    // Media below the first screen (reason videos, lazy images) in page order. Each one loads about a screen before
    // the visitor reaches it, and loading one also loads the next, so a reason is ready before it scrolls into view.
    // This starts a moment after the page has loaded, or as soon as the visitor starts scrolling (whichever comes
    // first), so the first screen keeps the bandwidth while it's being looked at.
    const steps = [...root.querySelectorAll('video[data-src]:not([data-hero]), img[loading="lazy"]')];
    const primed = new WeakSet();
    const load = el => {
      if (primed.has(el)) return;
      primed.add(el);
      if (el.tagName === 'IMG') { el.loading = 'eager'; return; }
      setPoster(el);
      if (!el.hasAttribute('src')) { el.preload = 'auto'; el.src = el.dataset.src; el.load(); }
    };
    const prime = el => {
      load(el);
      const next = steps.slice(steps.indexOf(el) + 1).find(step => step.getClientRects().length);
      if (next) load(next);
    };
    let started = false;
    const startPriming = () => {
      if (started) return;
      started = true;
      removeEventListener('scroll', startPriming);
      if (!('IntersectionObserver' in window)) { steps.forEach(load); return; }
      const ahead = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting) { prime(entry.target); ahead.unobserve(entry.target); }
      }), { rootMargin: '100% 0px' });
      steps.forEach(el => ahead.observe(el));
      cleanup.push(() => ahead.disconnect());
    };
    // Starting earlier (e.g. when the hero can play) put a reason video inside the first load and dropped PageSpeed
    // to ~50 on some runs; a short wait after 'load' keeps the first load exactly as before.
    const afterLoad = () => setTimeout(startPriming, 1000);
    if (scrollY > 0) startPriming();
    else if (document.readyState === 'complete') afterLoad();
    else {
      addEventListener('load', afterLoad, { once: true });
      addEventListener('scroll', startPriming, { passive: true });
      cleanup.push(() => { removeEventListener('load', afterLoad); removeEventListener('scroll', startPriming); });
    }
    // Videos (hero and any other one already on the first screen) start once the page has loaded (or on the first
    // tap/scroll, or after 4 s at the latest), together with the deferred trackers. Started earlier, the 1.8 MB hero file ran alongside the first screen and
    // PageSpeed counted it into the LCP load (LCP 8–20 s on some runs). The poster is the video's first frame, so the
    // picture is the same until it starts moving.
    let painted = document.readyState === 'complete';
    const afterPaint = () => { if (!painted) { painted = true; sync(); } };
    if (!painted) {
      const onLoad = () => (window.requestIdleCallback || (f => setTimeout(f, 1)))(afterPaint, { timeout: 1000 });
      addEventListener('load', onLoad, { once: true });
      ['pointerdown', 'touchstart', 'scroll'].forEach(t => addEventListener(t, afterPaint, { once: true, passive: true }));
      const fallback = setTimeout(afterPaint, 4000);
      cleanup.push(() => { removeEventListener('load', onLoad); clearTimeout(fallback); });
    }
    const play = video => {
      if (motion.matches || document.hidden || !visible.has(video)) { video.pause(); return; }
      if (!painted) return;
      setPoster(video);
      if (!video.hasAttribute('src')) { video.src = video.dataset.src; video.load(); }
      video.muted = true;
      const promise = video.play();
      if (promise) promise.catch(() => {});
    };
    const sync = () => videos.forEach(play);
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > .05) visible.add(entry.target);
          else visible.delete(entry.target);
          play(entry.target);
        }
      }, { threshold: [0, .05, .15] });
      videos.forEach(video => observer.observe(video));
      cleanup.push(() => observer.disconnect());
    } else {
      videos.filter(v => v.getClientRects().length).forEach(v => visible.add(v));
      sync();
    }
    document.addEventListener('visibilitychange', sync);
    motion.addEventListener('change', sync);
    cleanup.push(() => { document.removeEventListener('visibilitychange', sync); motion.removeEventListener('change', sync); videos.forEach(v => v.pause()); });
    root.querySelectorAll('[data-destination]').forEach(element => {
      const navigate = event => {
        const nested = event.target.closest('a,button,[data-destination]');
        if (nested !== element || (event.type === 'keydown' && event.key !== 'Enter')) return;
        location.assign(element.dataset.destination);
      };
      element.addEventListener('click', navigate);
      element.addEventListener('keydown', navigate);
    });
    root.cleanupNativePage = () => cleanup.forEach(fn => fn());
  }
  const scan = node => { if (node.matches?.('[data-tgn-page]')) init(node); node.querySelectorAll?.('[data-tgn-page]').forEach(init); };
  scan(document);
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-tgn-page]').forEach(root => root.cleanupNativePage?.()));
})();
